import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { ChevronLeft, CornerUpLeft } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  CrossTenantConversation,
  CrossTenantMessage,
  CrossTenantMessagePage,
} from '@up2d8/contracts';
import { Badge, Button, Card, EmptyState, LoadingState, Textarea, cn, useToast } from '@up2d8/ui';
import { useSession } from '../../../shared/session';
import { uiLocale } from '../../../shared/format';
import { renderAppTabLink, useAppTabs } from '../app-tabs';
import { DeskShell } from '../desk/desk-shell';
import { MessagesIcon } from '../messages-icon';
import { NotificationBell } from '../notification-bell';
import {
  ReactionPicker,
  ReactionPills,
  ReplyComposerBar,
  ReplyQuote,
  jumpToMessage,
  useReactToMessage,
} from '../chat-message-extras';

/**
 * Eén gesprek over bedrijven heen (ACC-4).
 *
 * Tekst, leesbevestiging, reacties en antwoorden (BER-8b) — met dezelfde onderdelen als een gewoon
 * gesprek. Bijlagen en zoeken nog niet. Bovenaan staat wie meedoet en uit welk bedrijf, zodat het
 * altijd zichtbaar is dat dit gesprek buiten het eigen bedrijf gaat.
 */
export function CrossTenantConversationPage() {
  const { t } = useTranslation();
  const { api } = useSession();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const tabs = useAppTabs();
  const { conversationId } = useParams({ strict: false }) as { conversationId: string };
  const [draft, setDraft] = useState('');
  const [replyTo, setReplyTo] = useState<CrossTenantMessage | null>(null);
  /** Bij welk bericht de emoji's en "Antwoorden" openstaan; een tik op de ballon wisselt. */
  const [actionsFor, setActionsFor] = useState<string | null>(null);
  const list = useRef<HTMLUListElement>(null);
  const react = useReactToMessage(conversationId, 'shared');

  useEffect(() => {
    setReplyTo(null);
    setActionsFor(null);
    // De half getypte zin hoort bij het vorige gesprek, niet bij dit.
    setDraft('');
  }, [conversationId]);

  const conversation = useQuery({
    queryKey: ['shared-conversation', conversationId],
    retry: false,
    queryFn: async (): Promise<CrossTenantConversation | null> => {
      const { data, response } = await api.GET('/api/v1/shared-conversations/{conversationId}', {
        params: { path: { conversationId } },
      });
      if (response.status === 404) return null;
      if (!data) throw new Error('Ophalen mislukt');
      return data as CrossTenantConversation;
    },
  });

  const messages = useQuery({
    queryKey: ['shared-messages', conversationId],
    enabled: Boolean(conversation.data),
    refetchInterval: 15_000,
    refetchOnWindowFocus: true,
    queryFn: async (): Promise<CrossTenantMessagePage> => {
      const { data, error } = await api.GET(
        '/api/v1/shared-conversations/{conversationId}/messages',
        { params: { path: { conversationId } } },
      );
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return data as CrossTenantMessagePage;
    },
  });

  const markRead = useMutation({
    mutationFn: async () => {
      await api.POST('/api/v1/shared-conversations/{conversationId}/read', {
        params: { path: { conversationId } },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['shared-conversations'] });
      void queryClient.invalidateQueries({ queryKey: ['shared-conversation', conversationId] });
      void queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
  });

  /*
    Markeren bij het openen, en opnieuw wanneer er een bericht van een ander binnenkomt terwijl het
    gesprek openstaat — zoals bij een gewoon gesprek. De mutatie via een ref: in de
    afhankelijkheidslijst zou die elke render een schrijfactie geven.
  */
  const markReadRef = useRef(markRead);
  markReadRef.current = markRead;
  const markedRef = useRef<string | null>(null);
  const seenIncomingRef = useRef<{ conversationId: string; id: string | null | undefined } | null>(
    null,
  );
  const unread = conversation.data?.unreadCount ?? 0;
  // Nieuwste eerst van de server; `undefined` zolang de berichten nog niet geladen zijn.
  const latestIncoming = messages.data
    ? (messages.data.items.find((message) => !message.mine)?.id ?? null)
    : undefined;
  useEffect(() => {
    const previous = seenIncomingRef.current;
    seenIncomingRef.current = { conversationId, id: latestIncoming };
    const fresh =
      previous?.conversationId === conversationId &&
      previous.id !== undefined &&
      latestIncoming != null &&
      previous.id !== latestIncoming;
    const key = `${conversationId}:${latestIncoming ?? ''}`;
    const openedUnread = unread > 0 && !markedRef.current?.startsWith(`${conversationId}:`);
    if (openedUnread || (fresh && markedRef.current !== key)) {
      markedRef.current = key;
      markReadRef.current.mutate();
    }
  }, [conversationId, unread, latestIncoming]);

  const send = useMutation({
    mutationFn: async (body: string) => {
      const { error } = await api.POST('/api/v1/shared-conversations/{conversationId}/messages', {
        params: { path: { conversationId } },
        body: { body, replyToId: replyTo?.id ?? null },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setDraft('');
      setReplyTo(null);
      void queryClient.invalidateQueries({ queryKey: ['shared-messages', conversationId] });
      void queryClient.invalidateQueries({ queryKey: ['shared-conversations'] });
    },
    onError: () => {
      toast.show({ tone: 'danger', title: t('messaging.sendFailed') });
    },
  });

  const items = [...(messages.data?.items ?? [])].reverse();
  const trimmed = draft.trim();
  const others = conversation.data?.participants.filter((participant) => !participant.me) ?? [];

  return (
    <DeskShell
      messages={<MessagesIcon />}
      bell={<NotificationBell />}
      tabs={tabs}
      activeKey="messages"
      renderLink={renderAppTabLink}
    >
      <div className="mx-auto flex w-full max-w-2xl flex-col gap-element">
        <button
          type="button"
          data-touch-target
          onClick={() => void navigate({ to: '/app/gesprekken' })}
          className="inline-flex min-h-touch items-center gap-1 self-start rounded-pill px-2 text-small text-text-secondary hover:text-brand-primary"
        >
          <ChevronLeft aria-hidden className="h-4 w-4" />
          {t('messaging.title')}
        </button>

        {conversation.isPending ? (
          <LoadingState rows={4} label={t('common.loading')} />
        ) : !conversation.data ? (
          <Card>
            <EmptyState
              title={t('crossTenant.notFound')}
              description={t('crossTenant.notFoundHint')}
            />
          </Card>
        ) : (
          <>
            <div>
              <h1 className="font-heading text-page-title font-semibold text-text-primary">
                {conversation.data.title}
              </h1>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <Badge tone="cyan">{t('crossTenant.badge')}</Badge>
                <span className="text-small text-text-secondary" data-testid="cross-tenant-members">
                  {others
                    .map((other) =>
                      t('crossTenant.personAt', {
                        name: other.displayName,
                        company: other.tenantName,
                      }),
                    )
                    .join(', ')}
                </span>
              </div>
            </div>

            {messages.isPending ? (
              <LoadingState rows={4} label={t('common.loading')} />
            ) : items.length === 0 ? (
              <Card>
                <EmptyState
                  title={t('messaging.noMessages')}
                  description={t('crossTenant.noMessagesHint')}
                />
              </Card>
            ) : (
              <ul ref={list} className="flex flex-col gap-2" data-testid="cross-tenant-messages">
                {items.map((message) => (
                  <li
                    key={message.id}
                    data-testid="cross-tenant-message"
                    data-message-id={message.id}
                    data-mine={message.mine}
                    className={cn('flex flex-col', message.mine ? 'items-end' : 'items-start')}
                  >
                    <div
                      role="button"
                      tabIndex={0}
                      aria-expanded={actionsFor === message.id}
                      onClick={(event) => {
                        // Een tik op het citaat springt; dat opent de acties niet.
                        if (
                          (event.target as HTMLElement).closest('[data-testid="chat-reply-quote"]')
                        )
                          return;
                        setActionsFor((open) => (open === message.id ? null : message.id));
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          setActionsFor((open) => (open === message.id ? null : message.id));
                        }
                      }}
                      className={cn(
                        'max-w-[85%] rounded-lg px-3 py-2',
                        message.mine
                          ? 'bg-brand-primary-tint text-text-primary'
                          : 'bg-surface-sunk text-text-primary',
                      )}
                    >
                      {!message.mine && (
                        <p className="text-caption font-medium text-text-secondary">
                          {t('crossTenant.personAt', {
                            name: message.senderDisplayName,
                            company: message.senderTenantName,
                          })}
                        </p>
                      )}
                      {message.replyTo && (
                        <ReplyQuote
                          reply={message.replyTo}
                          mine={false}
                          onJump={(id) => jumpToMessage(list.current, id)}
                        />
                      )}
                      {/* Platte tekst, nooit HTML. */}
                      <p className="whitespace-pre-wrap break-words text-content">{message.body}</p>
                      <p className="mt-0.5 text-right text-caption text-text-muted">
                        {new Date(message.createdAt).toLocaleTimeString(uiLocale(), {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                        {message.readByCount !== null
                          ? ` · ${t('crossTenant.readBy', { count: message.readByCount })}`
                          : ''}
                      </p>
                    </div>

                    <ReactionPills
                      message={message}
                      onReact={(emoji) => react.mutate({ messageId: message.id, emoji })}
                      className="mt-1"
                    />

                    {actionsFor === message.id && (
                      <div
                        data-testid="message-actions"
                        className={cn(
                          'mt-1 flex flex-wrap items-center gap-1.5',
                          message.mine && 'justify-end',
                        )}
                      >
                        <ReactionPicker
                          current={message.myReaction}
                          onPick={(emoji) => {
                            setActionsFor(null);
                            react.mutate({
                              messageId: message.id,
                              emoji: message.myReaction === emoji ? null : emoji,
                            });
                          }}
                        />
                        <button
                          type="button"
                          data-touch-target
                          data-testid="message-reply"
                          onClick={() => {
                            setReplyTo(message);
                            setActionsFor(null);
                            document.getElementById('cross-tenant-body')?.focus();
                          }}
                          className="inline-flex min-h-touch items-center gap-1.5 rounded-pill border border-border-light bg-surface px-3 text-small text-text-secondary shadow-sm hover:text-brand-primary"
                        >
                          <CornerUpLeft aria-hidden className="h-4 w-4" />
                          {t('messaging.reply')}
                        </button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}

            <form
              className="flex flex-col gap-element"
              onSubmit={(event) => {
                event.preventDefault();
                if (trimmed.length > 0) send.mutate(trimmed);
              }}
            >
              {replyTo && <ReplyComposerBar message={replyTo} onCancel={() => setReplyTo(null)} />}
              <Textarea
                id="cross-tenant-body"
                label={t('messaging.messageLabel')}
                placeholder={t('messaging.messagePlaceholder')}
                value={draft}
                maxLength={2000}
                onChange={(event) => setDraft(event.target.value)}
              />
              <Button
                type="submit"
                data-testid="cross-tenant-send"
                loading={send.isPending}
                disabled={trimmed.length === 0}
              >
                {t('messaging.send')}
              </Button>
            </form>
          </>
        )}
      </div>
    </DeskShell>
  );
}
